import { useEffect, useState, useCallback } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { getService } from "@/data/services";
import api from "@/lib/api";

const MAX_GALLERY = 10;

export default function ServiceDetail() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "").split("/")[0];
  const { i18n } = useTranslation();
  const isIT = i18n.language.startsWith("it");
  const service = getService(slug);
  const [lightbox, setLightbox] = useState(null);
  const [remoteGallery, setRemoteGallery] = useState(null); // null = loading

  useEffect(() => {
    if (!service) return;
    api.get(`/services/${slug}/gallery`)
      .then((r) => setRemoteGallery(r.data?.image_urls || []))
      .catch(() => setRemoteGallery([]));
  }, [slug, service]);

  const staticGallery = service?.gallery || [];
  const gallery = (remoteGallery && remoteGallery.length > 0 ? remoteGallery : staticGallery).slice(0, MAX_GALLERY);

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(() => setLightbox((i) => (i - 1 + gallery.length) % gallery.length), [gallery.length]);
  const next = useCallback(() => setLightbox((i) => (i + 1) % gallery.length), [gallery.length]);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, close, prev, next]);

  if (!service) return <Navigate to="/servizi" replace />;

  const Icon = service.icon;
  const title = isIT ? service.title_it : service.title_en;
  const desc = isIT ? service.desc_it : service.desc_en;
  const backLabel = isIT ? "Torna ai servizi" : "Back to services";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
      <Link
        to="/servizi"
        data-testid="service-back"
        className="inline-flex items-center gap-2 text-sm text-stone-700 hover:text-[#D06D4D] transition-colors"
      >
        <ArrowLeft size={16} /> {backLabel}
      </Link>

      <header className="mt-10 flex items-start gap-5">
        <div className="w-14 h-14 rounded-xl bg-[#D06D4D]/10 border border-[#D06D4D]/20 flex items-center justify-center flex-shrink-0">
          <Icon size={26} className="text-[#D06D4D]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-serif text-4xl lg:text-5xl text-stone-900" data-testid="service-title">
            {title}
          </h1>
          <div className="mt-3 w-16 h-[2px] bg-[#D06D4D]" aria-hidden="true" />
        </div>
      </header>

      <p className="mt-8 text-lg text-stone-700 leading-relaxed max-w-3xl" data-testid="service-desc">
        {desc}
      </p>

      {/* Gallery */}
      <section className="mt-16">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif text-2xl lg:text-3xl text-stone-900">
            {isIT ? "Galleria" : "Gallery"}
          </h2>
        </div>

        {gallery.length === 0 ? (
          <div className="mt-8 border border-dashed border-stone-300 rounded-xl p-12 text-center text-stone-500 bg-white" data-testid="service-gallery-empty">
            <ImageOff className="mx-auto text-stone-400" size={32} />
            <p className="mt-4 text-sm">
              {isIT
                ? "Nessuna fotografia disponibile al momento. Le foto reali di questo servizio verranno caricate a breve."
                : "No photos available yet. Real photos for this service will be uploaded shortly."}
            </p>
          </div>
        ) : (
          <div
            className="mt-8 grid grid-cols-3 sm:grid-cols-5 gap-2 md:gap-3"
            data-testid="service-gallery"
          >
            {gallery.map((src, i) => (
              <button
                key={src}
                type="button"
                data-testid={`service-gallery-${i}`}
                onClick={() => setLightbox(i)}
                className="group relative aspect-square overflow-hidden rounded-lg bg-stone-100 focus:outline-none focus:ring-2 focus:ring-[#D06D4D] focus:ring-offset-2"
              >
                <img
                  src={src}
                  alt={`${title} — ${i + 1}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Bottom back link */}
      <div className="mt-16">
        <Link
          to="/servizi"
          className="inline-flex items-center gap-2 text-sm border border-stone-900 text-stone-900 px-6 py-3 rounded-full hover:bg-stone-900 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> {backLabel}
        </Link>
      </div>

      {/* Lightbox */}
      {lightbox !== null && gallery[lightbox] && (
        <div
          data-testid="service-lightbox"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 bg-stone-950/95 flex items-center justify-center px-4"
          onClick={close}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="service-lightbox-close"
            aria-label="Chiudi"
            className="absolute top-5 right-5 text-white/80 hover:text-white p-2"
          >
            <X size={26} />
          </button>
          {gallery.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); prev(); }}
                data-testid="service-lightbox-prev"
                aria-label="Precedente"
                className="absolute left-3 md:left-6 text-white/80 hover:text-white p-2"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); next(); }}
                data-testid="service-lightbox-next"
                aria-label="Successiva"
                className="absolute right-3 md:right-6 text-white/80 hover:text-white p-2"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
          <img
            src={gallery[lightbox]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[85vh] object-contain rounded-sm shadow-2xl"
          />
          <div className="absolute bottom-6 text-xs uppercase tracking-widest text-white/60">
            {lightbox + 1} / {gallery.length}
          </div>
        </div>
      )}
    </div>
  );
}
